(function () {
  'use strict';

  class RequestError extends Error {
    constructor(code, retryable = false, retryAfterMs = 0) {
      super(code);
      this.code = code;
      this.retryable = retryable;
      this.retryAfterMs = retryAfterMs;
    }
  }

  function validateResponse(response, now) {
    if (response.redirected) throw new RequestError('session');
    if (response.ok) return;
    const retryAfter = response.headers?.get('Retry-After');
    const delay = retryAfter
      ? Math.max(0, Number.isFinite(Number(retryAfter)) ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - now()) || 0
      : 0;
    const status = response.status;
    if (status === 401 || status === 403) throw new RequestError('session');
    if (status === 404 || status === 410) throw new RequestError('unavailable');
    if (status === 429) throw new RequestError('rateLimit', true, delay);
    if (status === 408 || status === 504) throw new RequestError('timeout', true, delay);
    throw new RequestError(status >= 500 ? 'server' : 'invalid', status >= 500, delay);
  }

  /** Owns the complete request deadline, including body consumption and cancellation. */
  class SectionRequestClient {
    constructor({ fetchImplementation = (...args) => fetch(...args), now = () => Date.now(),
      schedule = (...args) => setTimeout(...args), cancel = handle => clearTimeout(handle), checkResponse = validateResponse } = {}) {
      Object.assign(this, { fetchImplementation, now, schedule, cancel, checkResponse });
    }

    async get(url, { signal, read, timeoutMs = 15000 } = {}) {
      if (signal?.aborted) throw new RequestError('cancelled');
      const controller = new AbortController();
      let timer;
      let abort;
      const interruption = new Promise((resolve, reject) => {
        abort = () => {
          reject(new RequestError('cancelled'));
          controller.abort();
        };
        signal?.addEventListener('abort', abort, { once: true });
        timer = this.schedule(() => {
          reject(new RequestError('timeout', true));
          controller.abort();
        }, timeoutMs);
      });
      try {
        const content = (async () => {
          const response = await this.fetchImplementation(url.toString(), { signal: controller.signal, cache: 'no-store' });
          this.checkResponse(response, this.now);
          return read(response);
        })();
        return await Promise.race([content, interruption]);
      } catch (error) {
        if (error instanceof RequestError) throw error;
        if (error.name === 'SyntaxError') throw new RequestError('invalid');
        throw new RequestError('network', true);
      } finally {
        this.cancel(timer);
        signal?.removeEventListener('abort', abort);
      }
    }
  }

  /** Retry policy is independent of HTTP, HTML, and the component's status UI. */
  class RetryPolicy {
    constructor({ attempts = 3, maximumDelay = 10000, now = () => Date.now(),
      isOnline = () => navigator.onLine !== false, schedule = (...args) => setTimeout(...args), cancel = handle => clearTimeout(handle) } = {}) {
      Object.assign(this, { attempts, maximumDelay, now, isOnline, schedule, cancel });
      this.availableAt = 0;
    }

    async execute(operation, { signal, onRetry = () => {}, shouldContinue = () => true }) {
      for (let attempt = 0; attempt < this.attempts; attempt++) {
        if (signal.aborted || !shouldContinue()) throw new RequestError('cancelled');
        try {
          return await operation();
        } catch (error) {
          if (signal.aborted || !shouldContinue()) throw new RequestError('cancelled');
          if (!this.isOnline()) throw new RequestError('offline');
          const failure = error instanceof RequestError ? error : new RequestError('network', true);
          this.availableAt = this.now() + (failure.retryAfterMs || 0);
          if (!failure.retryable || attempt === this.attempts - 1 || failure.retryAfterMs > this.maximumDelay) throw failure;
          onRetry();
          await this.wait(Math.max(1000 * (2 ** attempt), failure.retryAfterMs || 0), signal);
        }
      }
    }

    wait(delay, signal) {
      return new Promise((resolve, reject) => {
        const abort = () => {
          this.cancel(timer);
          signal.removeEventListener('abort', abort);
          reject(new RequestError('cancelled'));
        };
        const timer = this.schedule(() => {
          signal.removeEventListener('abort', abort);
          resolve();
        }, delay);
        signal.addEventListener('abort', abort, { once: true });
        if (signal.aborted) abort();
      });
    }
  }

  const parseHTML = markup => {
    const fragment = document.createElement('div');
    fragment.innerHTML = markup;
    return fragment;
  };

  class SearchSectionRepository {
    constructor({ endpoint, sectionId, selector, parameters = {}, client = new SectionRequestClient(),
      parse = parseHTML, baseUrl = () => window.location.href }) {
      Object.assign(this, { endpoint, sectionId, selector, parameters, client, parse, baseUrl });
    }

    async get(query, { signal }) {
      const url = new URL(this.endpoint(), this.baseUrl());
      Object.entries(this.parameters).forEach(([name, value]) => url.searchParams.set(name, value));
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', this.sectionId);
      return this.client.get(url, { signal, timeoutMs: 12000, read: async response => {
        const section = this.parse(await response.text()).querySelector(this.selector);
        if (!section) throw new RequestError('invalid');
        return section.innerHTML;
      } });
    }
  }

  class SectionRepository {
    constructor({ client = new SectionRequestClient(), parse = parseHTML } = {}) {
      Object.assign(this, { client, parse });
    }

    async get({ url, sectionId, signal }) {
      const endpoint = new URL(url);
      endpoint.searchParams.set('sections', sectionId);
      const sections = await this.client.get(endpoint, { signal, timeoutMs: 12000, read: response => response.json() });
      if (typeof sections?.[sectionId] !== 'string') throw new RequestError('invalid');
      return this.parse(sections[sectionId]);
    }
  }

  const PaginationRepository = SectionRepository;

  class ListingSortRepository {
    constructor({ sections = new SectionRepository(), selector = '.main_collection' } = {}) {
      Object.assign(this, { sections, selector });
    }

    async get(request) {
      const section = await this.sections.get(request);
      const results = section.querySelector(this.selector);
      if (!results) throw new RequestError('invalid');
      return results.innerHTML;
    }
  }

  class ProductRecommendationRepository {
    constructor({ client = new SectionRequestClient(), parse = parseHTML, baseUrl = () => window.location.href } = {}) {
      Object.assign(this, { client, parse, baseUrl });
    }

    async get({ url, productId, sectionId, signal, allowEmpty = false }) {
      const endpoint = new URL(url, this.baseUrl());
      endpoint.searchParams.set('product_id', productId);
      endpoint.searchParams.set('section_id', sectionId);
      const markup = await this.client.get(endpoint, { signal, read: response => response.text() });
      const recommendations = this.parse(markup).querySelector('product-recommendations');
      if (!recommendations) throw new RequestError('invalid');
      if (!recommendations.querySelector('.product_recommendations_inner, .complementary_products_inner')) {
        if (recommendations.dataset.state === 'empty' || allowEmpty) return { empty: true };
        throw new RequestError('invalid');
      }
      return { empty: false, markup: recommendations.innerHTML };
    }
  }

  const api = { RequestError, SectionRequestClient, RetryPolicy, SearchSectionRepository, SectionRepository, PaginationRepository, ListingSortRepository, ProductRecommendationRepository };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.HOMSectionRequests = api;
})();
